// Top-up plan BUILDER + customer tap-to-pay entry points (tv-1a9, Feature B).
// Two mutating flows sit either side of tv-opw's reconciler and tv-qta's chase:
//
//   createTopupPlan     the agent builds the pay-over-time plan inside the
//                       deposit-send modal (a start date, a cadence, a
//                       per-instalment amount). The first instalment (>= $50)
//                       holds the date; the rest come via tap-to-pay.
//   payTopupInstalment  the CUSTOMER pays the next instalment from the hosted
//                       tap-to-pay page. Distinct from tv-qta's topupChasePays,
//                       which is the agent CHASE-context pay.
//
// Follows the *Flow.ts convention: read the store, mutate via the brain-store
// upserts, appendActivity, emit on the eventBus. "Now" is the sim clock, never
// Date.now(); money is whole AUD dollars. Reuses tv-w98's pure helpers.

import { useBrainStore } from '@/state/brainStore';
import { useSimulationStore } from '@/state/simulationStore';
import { useSessionStore } from '@/state/sessionStore';
import { useUIStore } from '@/state/uiStore';
import { eventBus } from '@/events/eventBus';
import { isStoreFallbackEnabled } from '@/api/apiMode';
import { payTopupInstalmentIntent } from '@/api/intents';
import { withServerAuthoredWrites } from '@/state/persistenceBridge';
import { attachTopupPlanToPendingHold, leadStageBookedCustomer, mirrorLeadStageBooked } from '@/domain/depositFlow';
import { generateInstalments, isTopupPlanComplete, MIN_TOPUP_INSTALMENT_AUD } from '@/domain/topupPlan';
import { depositPaidAud } from '@/utils/bookingSummary';
import type {
  Booking,
  Deposit,
  DepositLedgerEntry,
  TopupFrequency,
  TopupInstalment,
  TopupPlan,
} from '@/domain/types';

function nowIso(): string {
  return useSimulationStore.getState().clockIso;
}

/**
 * The signed-in operator's agent id (e.g. 'agent-cameron'), for plan attribution.
 * `created_by_agent_id` is an FK to app.agents(legacy_slug), so it must be a real
 * agent id — never the persona role slug ('bookings-agent') or a surface label
 * ('Console'), which 500 the plan PUT and lose the plan (tv-2na.1). Mirrors
 * depositFlow's currentAgentId.
 */
function currentAgentId(): string | undefined {
  return useSessionStore.getState().agentId ?? undefined;
}

/**
 * Plan ids with a server-backed pay-topup-instalment intent currently in flight
 * (tv-rp7.4). The instalment pay now lands ASYNC (in the intent's `.then`), so a
 * second tap for the SAME plan within the round-trip must NOT fire a second intent
 * nor double-apply the mirror/side-effects. Keyed on the stable planId — added
 * before the intent call, removed when it settles. (Mirrors depositFlow's
 * inFlightPayDeposits.)
 */
const inFlightTopupPays = new Set<string>();

/**
 * The builder's floor: an instalment below this is refused. Single source of
 * truth is `MIN_TOPUP_INSTALMENT_AUD` in topupPlan.ts — re-exported here under
 * the builder's historical name (tv-6qv.14: the two constants used to be
 * declared separately and could drift).
 */
export const MIN_INSTALMENT_AUD = MIN_TOPUP_INSTALMENT_AUD;

/**
 * Build + persist a pay-over-time plan for a deposit. `totalAud` defaults to the
 * deposit's `amountAud` (the full deposit; the first instalment >= $50 holds the
 * date). Mints `topup-<depositId>`, derives customerId / bookingId
 * (`deposit.bookingId ?? deposit.heldBookingId`) / studioId from the deposit, and
 * lays the instalment schedule via `generateInstalments`, and attributes the plan
 * to the signed-in agent (`currentAgentId`). Guard: the deposit must exist,
 * `totalAud` must be positive and `instalmentAud` at least $50.
 */
export function createTopupPlan(opts: {
  depositId: string;
  startIso: string;
  frequency: TopupFrequency;
  instalmentAud: number;
  totalAud?: number;
  at?: string;
}): { ok: boolean; planId?: string } {
  const brain = useBrainStore.getState();
  const deposit = brain.deposits[opts.depositId];
  if (!deposit) return { ok: false };

  const totalAud = opts.totalAud ?? deposit.amountAud;
  if (totalAud <= 0) return { ok: false };
  if (opts.instalmentAud < MIN_INSTALMENT_AUD) return { ok: false };

  const at = opts.at ?? nowIso();
  const createdByAgentId = currentAgentId();
  const planId = `topup-${deposit.id}`;
  const bookingId = deposit.bookingId ?? deposit.heldBookingId;
  const instalments = generateInstalments({
    startIso: opts.startIso,
    frequency: opts.frequency,
    instalmentAud: opts.instalmentAud,
    totalAud,
  });

  const plan: TopupPlan = {
    id: planId,
    depositId: deposit.id,
    customerId: deposit.customerId,
    studioId: deposit.studioId,
    totalAud,
    frequency: opts.frequency,
    startIso: opts.startIso,
    instalments,
    status: 'active',
    createdAt: at,
    ...(bookingId ? { bookingId } : {}),
    ...(createdByAgentId ? { createdByAgentId } : {}),
  };
  // A plan built in the same turn as its deposit's hold-slot intent rides INSIDE
  // that intent (one transaction) rather than a PUT racing the deposit FK
  // (tv-2na.7); otherwise it is an ordinary write-through upsert.
  if (!attachTopupPlanToPendingHold(plan)) brain.upsertTopupPlan(plan);

  // Reuse an existing activity type (deposit-link-sent): a payment arrangement
  // for a deposit went out. No new ActivityFeedEntry union member.
  brain.appendActivity({
    id: `act-topup-plan-${planId}-${at}`,
    type: 'deposit-link-sent',
    customerId: deposit.customerId,
    studioId: deposit.studioId,
    artistId: deposit.artistId,
    amount: totalAud,
    body: `Pay-over-time plan set up: ${instalments.length} ${opts.frequency} instalments totalling $${totalAud}.`,
    timestamp: at,
  });
  eventBus.publish('topup:plan-created', {
    planId,
    depositId: deposit.id,
    customerId: deposit.customerId,
    totalAud,
    instalments: instalments.length,
    at,
  });
  return { ok: true, planId };
}

/**
 * Pay the earliest unpaid instalment (the customer tap-to-pay entry point). Marks
 * the earliest `scheduled` / `reminded` / `missed` instalment `paid` (paidAt=at),
 * appends a `topup` ledger movement to the deposit so its paid balance grows by
 * the instalment amount (whole dollars), and flips the plan to `completed` once
 * every instalment is settled. Publishes `topup:instalment-paid`. Guards: unknown
 * plan, or a plan that is no longer active, return `{ ok: false }`.
 *
 * A $50+ partial is a REAL booking (board 02, 13 Aug; tv-2na.8.10): the pay that
 * brings the paid balance to $50+ (the first instalment, by the builder's floor)
 * SECURES the deposit — pending -> paid, the app's part-paid state (paid with
 * less than the full amount in), locked to its booking — and firms the held
 * booking (confirmed, not over-bookable). So the 24h link sweep never touches it.
 * Mirrors runPayTopupInstalment (apps/api payTopupInstalment.ts) exactly.
 */
export function payTopupInstalment(
  planId: string,
  opts?: {
    by?: string;
    at?: string;
    /** 7ktj: told once the pay has truly landed (true) or been refused (false). */
    onSettled?: (ok: boolean) => void;
  },
): { ok: boolean; amountAud?: number } {
  const brain = useBrainStore.getState();
  const plan = brain.topupPlans[planId];
  if (!plan) return { ok: false };
  if (plan.status !== 'active') return { ok: false };

  const at = opts?.at ?? nowIso();
  const by = opts?.by ?? 'customer';

  const idx = plan.instalments.findIndex(
    (i) => i.status === 'scheduled' || i.status === 'reminded' || i.status === 'missed',
  );
  if (idx === -1) return { ok: false };

  const instalment = plan.instalments[idx]!;
  const instalments: TopupInstalment[] = plan.instalments.map((inst, i) =>
    i === idx ? { ...inst, status: 'paid', paidAt: at } : inst,
  );
  const nextPlan: TopupPlan = { ...plan, instalments };
  const localStatus: TopupPlan['status'] = isTopupPlanComplete(nextPlan) ? 'completed' : plan.status;

  // Grow the deposit's paid balance by this instalment. Preserve the prior paid
  // history (backfill a `paid` entry when the deposit has no ledger yet) so the
  // ledger-derived balance rises rather than replacing what was already in. Built
  // ONCE here — both the store-fallback write and the server mirror reuse it.
  const deposit = brain.deposits[plan.depositId];
  const grownDeposit: Deposit | undefined = deposit
    ? (() => {
        const priorPaid = depositPaidAud(deposit);
        const baseLedger: DepositLedgerEntry[] =
          deposit.ledger && deposit.ledger.length > 0
            ? deposit.ledger
            : priorPaid > 0
              ? [{ at: deposit.paidAt ?? at, amountAud: priorPaid, kind: 'paid', by: deposit.takenByAgentId ?? 'system' }]
              : [];
        return {
          ...deposit,
          amountPaid: priorPaid + instalment.amountAud,
          ledger: [
            ...baseLedger,
            { at, amountAud: instalment.amountAud, kind: 'topup', by, note: 'Top-up instalment paid' },
          ],
        };
      })()
    : undefined;

  // The part-paid rule (see the doc above), for the store-fallback write. The
  // server-backed path mirrors what the intent reports instead.
  const heldSlug = deposit?.bookingId ?? deposit?.heldBookingId ?? plan.bookingId;
  const secure = (d: Deposit): Deposit => ({
    ...d,
    status: 'paid',
    paidAt: d.paidAt ?? at,
    ...(heldSlug ? { bookingId: heldSlug, heldBookingId: undefined } : {}),
  });
  const firmBooking = (): Booking | undefined => {
    const held = heldSlug ? brain.bookings[heldSlug] : undefined;
    if (!held || !['held', 'pending-deposit'].includes(held.status)) return undefined;
    return { ...held, status: 'confirmed', overbookable: false };
  };
  const localSecures =
    !!grownDeposit &&
    deposit!.status !== 'paid' &&
    (localStatus === 'completed' ||
      (deposit!.status === 'pending' && (grownDeposit.amountPaid ?? 0) >= MIN_TOPUP_INSTALMENT_AUD));

  // Side-effects that ride EVERY pay (the activity + the event) — separate from
  // the plan/deposit/booking the intent OWNS, so they persist through the NORMAL
  // path in both modes. `completed` gates the "paid in full" copy + the event flag.
  const runSideEffects = (completed: boolean): void => {
    brain.appendActivity({
      id: `act-topup-instalment-${plan.id}-${at}`,
      type: 'deposit-paid',
      customerId: plan.customerId,
      studioId: plan.studioId,
      amount: instalment.amountAud,
      body: completed
        ? `Final instalment paid: $${instalment.amountAud}. Plan paid in full.`
        : `Instalment paid: $${instalment.amountAud}.`,
      timestamp: at,
    });
    eventBus.publish('topup:instalment-paid', {
      planId: plan.id,
      depositId: plan.depositId,
      amountAud: instalment.amountAud,
      completed,
      at,
    });
  };

  // SERVER-BACKED (the served build / E2E): the ATOMIC pay-topup-instalment intent
  // owns the plan + deposit (+ booking on completion) flip in ONE transaction
  // (tv-rp7.4). On the FINAL instalment it also confirms the held booking — the
  // tv-rcx fix the client-only path below MISSED. The flip lands only after the
  // intent confirms; a concurrent replay never double-fires; an idempotent
  // `alreadyComplete` mirrors nothing new; a failed intent applies nothing and
  // surfaces the failure to BOTH the customer flow and staff.
  if (!isStoreFallbackEnabled()) {
    // Close the concurrency window synchronously: a second tap for this plan within
    // the round-trip must NOT fire a second intent nor double-apply.
    if (inFlightTopupPays.has(planId)) return { ok: false };
    inFlightTopupPays.add(planId);

    // Mirror the server's committed end state into the store WITHOUT a second PUT
    // (withServerAuthoredWrites) so the mounted page patches optimistically. On
    // completion the deposit also flips paid+paidAt and the held booking goes
    // confirmed + firm (not overbookable) — exactly what the server writes.
    const applyMirror = (completed: boolean, bookingConfirmed: boolean, depositSecured: boolean): void => {
      const pairs: Array<[string, string]> = [['topupPlans', planId]];
      if (grownDeposit) pairs.push(['deposits', plan.depositId]);

      const confirmedBooking = bookingConfirmed ? firmBooking() : undefined;
      if (confirmedBooking) pairs.push(['bookings', confirmedBooking.id]);
      // The intent books the lead when this pay secures the deposit (tv-2na.8.12).
      const bookedCustomer = depositSecured ? leadStageBookedCustomer(plan.customerId) : undefined;
      if (bookedCustomer) pairs.push(['customers', bookedCustomer.id]);

      withServerAuthoredWrites(pairs, () => {
        if (bookedCustomer) brain.upsertCustomer(bookedCustomer);
        brain.upsertTopupPlan({ ...nextPlan, status: completed ? 'completed' : plan.status });
        if (grownDeposit) brain.upsertDeposit(depositSecured ? secure(grownDeposit) : grownDeposit);
        if (confirmedBooking) brain.upsertBooking(confirmedBooking);
      });
    };

    // Surface an intent failure to BOTH audiences. The customer topup pay route
    // renders outside AppShell (no toast there): publish the failure event the pay
    // page resets on, AND keep the staff toast for the in-shell (console) case. The
    // plan is left untouched — nothing was charged/committed.
    const onPayFailed = (message?: string): void => {
      eventBus.publish('stripe-webhook-failed', { depositId: plan.depositId });
      useUIStore.getState().pushNotification({
        intensity: 'critical',
        title: "Couldn't take the payment",
        body: message ?? "The payment didn't go through, so nothing was charged. Please try again.",
        ttlMs: 10000,
      });
    };

    void payTopupInstalmentIntent(planId)
      .then((res) => {
        if (res.ok) {
          // An idempotent replay (alreadyComplete) means the plan was already
          // settled server-side — mirror nothing new and do NOT re-run side-effects.
          if (res.alreadyComplete) return opts?.onSettled?.(true);
          applyMirror(res.complete, res.bookingConfirmed, res.depositSecured);
          runSideEffects(res.complete);
          opts?.onSettled?.(true);
        } else {
          onPayFailed(res.message);
          opts?.onSettled?.(false);
        }
      })
      .catch((err) => {
        // The .then body (store mirror / side-effects) can throw; never leave an
        // unhandled rejection or strand the customer on a false success.
        console.error('[pay-topup-instalment] pay failed', err);
        onPayFailed();
        opts?.onSettled?.(false);
      })
      .finally(() => {
        inFlightTopupPays.delete(planId);
      });

    // Optimistic success for the (synchronous) caller — the console reads `.ok`;
    // the actual flip / any failure lands asynchronously above.
    return { ok: true, amountAud: instalment.amountAud };
  }

  // STORE-FALLBACK (unit tests / standalone): no API server, so the client applies
  // the same end state the intent commits — the persistence bridge PUTs it.
  brain.upsertTopupPlan({ ...nextPlan, status: localStatus });
  if (grownDeposit) brain.upsertDeposit(localSecures ? secure(grownDeposit) : grownDeposit);
  const firmed = localSecures || deposit?.status === 'paid' ? firmBooking() : undefined;
  if (firmed) brain.upsertBooking(firmed);
  if (localSecures) mirrorLeadStageBooked(plan.customerId);
  runSideEffects(localStatus === 'completed');
  opts?.onSettled?.(true);
  return { ok: true, amountAud: instalment.amountAud };
}
